import { useEffect, useRef } from 'react';
import { Notice } from '../ui/Notice';
import { withConflictConfirm } from '../availability/conflicts';
import './quotes.css';
import { api, ApiError } from '../api';
import type { VersionSummary } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * Owner 2026-10-22 01:25 — "Uzupełnij" (the quote from the packlist): first a choice (a new version or overwrite the
 * current one — a choice, not a "czy na pewno"), afterwards a short green "gotowe" notice that closes itself.
 */

/** What filling answers (F25; `removed`: items the packlist no longer has, owner 2026-10-22). */
export type FillResult = { versionId: string; added: number; updated: number; unchanged: number; removed?: number; skipped: string[]; created?: boolean };

/** "Kosztorys uzupełniony ze sprzętówki (dodano 3, zmieniono 2)." — with what was removed or skipped. */
export function fillText(r: FillResult): string {
  const parts = [`dodano ${r.added}`, `zmieniono ${r.updated}`];
  if ((r.removed ?? 0) > 0) parts.push(`usunięto ${r.removed}`);
  if (r.skipped.length > 0) parts.push(`pominięto ${r.skipped.length} — usunięte z katalogu`);
  return `${r.created ? 'Utworzono kosztorys ze sprzętówki' : 'Kosztorys uzupełniony ze sprzętówki'} (${parts.join(', ')}).`;
}

/** A version to start a new one from (the newest, sent or decided). */
export type NoDraft = { quoteId: string; versionId: string };

/** Re-review M3: someone made a draft while the person chose "Dodaj jako nową wersję": the choice comes again, with it. */
export class DraftCreatedError extends Error {
  constructor(readonly draft: VersionSummary) {
    super(`W międzyczasie powstała wersja robocza v${draft.number} — wybierz jeszcze raz.`);
  }
}

/**
 * "Dodaj jako nową wersję" / "Nowa wersja ze sprzętówki": a new draft from the given version, then filled from the
 * packlist. A draft someone made a moment ago is never overwritten without asking (`DraftCreatedError`).
 */
export async function newVersionFromPacklist(n: NoDraft): Promise<FillResult | null> {
  let draftId: string;
  try {
    draftId = (await api<{ id: string }>(`/api/quotes/quotes/${n.quoteId}/new-version`, { method: 'POST', body: { fromVersionId: n.versionId } })).id;
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 && error.code === 'draft_exists') {
      const d = error.current as { id: string; number: number };
      throw new DraftCreatedError({ id: d.id, number: d.number, state: 'robocza', outdated: false, sentAt: null });
    }
    throw error;
  }
  return fillDraftFromPacklist(draftId);
}

/**
 * "Uzupełnij ze sprzętówki" into a draft; gear other events reserved first asks „Dodaj mimo to” / „Anuluj” first
 * (owner 2026-10-24): null — cancelled, nothing filled.
 */
export function fillDraftFromPacklist(draftId: string): Promise<FillResult | null> {
  return withConflictConfirm((confirmConflicts) => api<FillResult>(`/api/quotes/versions/${draftId}/from-packlist`, { method: 'POST', body: confirmConflicts ? { confirmConflicts } : {} }));
}

const FROZEN_WORDS: Partial<Record<VersionSummary['state'], string>> = { wyslana: 'wysłana do klienta', zaakceptowana: 'zaakceptowana przez klienta', odrzucona: 'odrzucona przez klienta' };

/**
 * "Uzupełnij kosztorys ze sprzętówki": the newest version a draft — "Nadpisz obecną wersję (vN)"; "Dodaj jako nową
 * wersję" is there but off, saying why (a quote has one draft; a new version is made from a sent one). The newest
 * sent, accepted or rejected — only "Dodaj jako nową wersję (vN+1)" ("Nowa wersja ze sprzętówki"), and why the version
 * itself is not changed. Shared dialog markup (it opens in the middle of the event card); Escape closes only it.
 */
export function FillChoiceDialog({
  newest,
  busy,
  error,
  onOverwrite,
  onNewVersion,
  onClose,
  title = 'Uzupełnij kosztorys ze sprzętówki',
  text = 'Sprzęt w kosztorysie stanie się taki jak w sprzętówce: brakujący dojdzie, ilości się wyrównają, a sprzęt, którego w sprzętówce już nie ma, zniknie. Usługi, transport, pakiety, pozycje własne i od dostawcy zostają.',
  featureId = 'quotes.from_packlist',
}: {
  newest: VersionSummary;
  busy: boolean;
  /** Obsada → kosztorys: the same choice for "Uzupełnij z obsady" (its own title and what it does). */
  title?: string;
  text?: string;
  featureId?: string;
  /** Re-review M5: why filling failed, shown in the dialog. */
  error?: string | undefined;
  onOverwrite: () => void;
  onNewVersion: () => void;
  onClose: () => void;
}) {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  const draft = newest.state === 'robocza';
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="q-fill-title" data-feature-id={featureId}>
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="q-fill-title">{title}</h2>
        <p>{text}</p>
        {draft ? (
          <p className="hint">Nowa wersja powstaje z wersji wysłanej do klienta — v{newest.number} jest jeszcze robocza, więc uzupełnia się ją samą.</p>
        ) : (
          <p className="hint">
            v{newest.number} jest {FROZEN_WORDS[newest.state]} — tej wersji się nie zmienia. Uzupełniona będzie nowa wersja v{newest.number + 1}.
          </p>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          {draft && (
            <button ref={first} className="btn primary" type="button" disabled={busy} onClick={onOverwrite}>
              Nadpisz obecną wersję (v{newest.number})
            </button>
          )}
          <button ref={draft ? undefined : first} className={draft ? 'btn' : 'btn primary'} type="button" disabled={busy || draft} onClick={onNewVersion}>
            {draft ? 'Dodaj jako nową wersję' : `Dodaj jako nową wersję (v${newest.number + 1})`}
          </button>
          <button className="btn ghost" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/** How long the green "gotowe" notice stays (owner: show it, then close it — not kept on top). */
export const DONE_NOTICE_MS = 6000;

/** The green notice after something is done (docs/wyglad.md rule 2): it closes itself, or with its "×". */
export function DoneNotice({ text, onClose, ms = DONE_NOTICE_MS, featureId }: { text: string; onClose: () => void; ms?: number; featureId?: string }) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const timer = setTimeout(() => close.current(), ms);
    return () => clearTimeout(timer);
  }, [text, ms]);
  return (
    // owner 2026-10-27 21:02: at the bottom of the page, with the shared „×”
    <Notice className="q-done" {...(featureId ? { 'data-feature-id': featureId } : {})} onClose={onClose}>
      {text}
    </Notice>
  );
}
